import {
  CompanyLogoIcon,
  EducationLevelIcon,
  ExperienceLevelIcon,
  HiringNeedIcon,
  LanguagesIcon,
  LocationTypeIcon,
  PositionTypeIcon,
  SalaryRangeIcon,
} from 'assets';
import {
  FilledRectangleIcon,
  OutlinedRectangleIcon,
} from 'view/pages/profile/TestPage/MultiplySelect';
import { AccordionDetails, AccordionSummary, LinearProgress, styled, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SyntheticEvent, useState } from 'react';
import { Button, StatusBlock } from 'view/components';
import { getMatchScoreColor } from 'view/pages/profile/components/JobPostsListItem';
import { useContextSelector as useCS } from 'use-context-selector';
import { TUpdate } from 'domain/types';
import {
  CreateJobPostConfigContext,
  TCreateJobPostContextState,
} from 'domain/createJobPostConfig/CreateJobPostContext';
import { createJobPostConfigSelectors } from 'domain/createJobPostConfig/selectors';
import { JobSeekerApplyForJobPostModal } from 'view/modals';
import { useNavigate } from 'react-router';
import { GlobalConfigContext } from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Link } from 'react-router-dom';
import * as S from './styled';

const yearsOfExperienceOptions = [
  {
    value: 'Less than 1 year',
    label: 'Less than 1 year',
    experienceYearsMin: 0,
    experienceYearsMax: 1,
  },
  {
    value: '1-2 years',
    label: '1-2 years',
    experienceYearsMin: 1,
    experienceYearsMax: 2,
  },
  { value: '3-5 years', label: '3-5 years', experienceYearsMin: 3, experienceYearsMax: 5 },
  { value: '6-10 years', label: '6-10 years', experienceYearsMin: 6, experienceYearsMax: 10 },
  {
    value: 'More than 10 years',
    label: 'More than 10 years',
    experienceYearsMin: 10,
    experienceYearsMax: 100,
  },
];

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => {
  return {
    flex: 1,
    height: 10,
    borderRadius: 5,
    border: '1px solid #D8DBE8',
    backgroundColor: '#fff',

    [theme.breakpoints.down('tablet')]: {
      flex: 'initial',
      width: '100%',
    },
  };
});

const getPositionTypes = (positionTypes: any) => {
  if (!positionTypes?.length) return 'No position types';
  return positionTypes
    .map((positionType: any) => {
      return positionType.name;
    })
    .join(', ');
};

const getLanguages = (languages: any) => {
  if (!languages?.length) return 'No languages';
  return languages
    .map((language: any) => {
      return language.name;
    })
    .join(', ');
};

const getJobLocation = (jobLocation: any) => {
  if (!jobLocation) return 'No location type';
  const locations: Array<string> = [];

  if (jobLocation.onSite) locations.push('On Site');
  if (jobLocation.remote) locations.push('Remote');
  if (jobLocation.hybrid) locations.push('Hybrid');

  return locations?.length > 0 ? `${locations.join(', ')}` : 'No location type';
};

const ViewJobPostBlock = ({ data, getJobPostInfoById = () => {}, isPublic = false }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | false>('panel1');
  const [open, setOpen] = useState<boolean>(false);
  const [openBottom, setOpenBottom] = useState<boolean>(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const handleTooltipBottomClose = () => {
    setOpenBottom(false);
  };

  const handleTooltipBottomOpen = () => {
    setOpenBottom(true);
  };

  const { jobSeekerProfile } = useCS(GlobalConfigContext, globalConfigSelectors.userData);

  const update: TUpdate<TCreateJobPostContextState> | undefined = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.update
  );

  const params = new URLSearchParams(window.location.search);
  const matchScore = params.get('matchScore');

  const handleChange = (panel: string) => {
    return (event: SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };
  };

  return (
    <S.CandidateProfileBlock>
      <S.JobPostCardPreviewMainBlockHeader>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '20px',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <S.JobPostCardPreviewMainBlockHeaderTitle component="h2">
            {data?.jobTitle || 'No Job Title'}
          </S.JobPostCardPreviewMainBlockHeaderTitle>
          {data?.applicationStatus?.statusForCandidate ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <StatusBlock appStatus={data?.applicationStatus?.statusForCandidate} />
              {data?.applicationStatus?.statusForCandidate === 'Approved' && (
                <Button
                  variant="contained"
                  text="Go to Chat"
                  style={{ width: 'max-content', height: 'max-content' }}
                  onClick={() => {
                    navigate(
                      data?.applicationStatus?.chatId
                        ? `/messenger/chats?conversationId=${data?.applicationStatus?.chatId}`
                        : '/messenger/chats'
                    );
                  }}
                  disabled={!data?.applicationStatus?.chatId}
                />
              )}
            </div>
          ) : (
            <ClickAwayListener onClickAway={handleTooltipClose}>
              <div>
                <Tooltip
                  onClose={handleTooltipClose}
                  open={open}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  slotProps={{
                    popper: {
                      disablePortal: true,
                    },
                  }}
                  title={
                    !jobSeekerProfile || !jobSeekerProfile.hasWorkPreferencesFilled ? (
                      <div style={{ padding: '5px' }}>
                        <h3 style={{ marginBottom: '8px' }}>Incomplete Profile</h3>
                        <p>
                          You can’t apply for this position because your profile is incomplete.
                          Please complete your profile before applying.
                        </p>
                      </div>
                    ) : (
                      ''
                    )
                  }
                  arrow
                  placement="top"
                >
                  <div onClick={isPublic ? undefined : handleTooltipOpen}>
                    {/*  */}
                    <Button
                      variant="contained"
                      text={isPublic ? 'Sign In to Apply' : 'Apply now'}
                      style={{ width: 'max-content', height: 'max-content' }}
                      onClick={() => {
                        if (isPublic) {
                          navigate('/auth/login');
                        } else if (
                          update &&
                          jobSeekerProfile &&
                          jobSeekerProfile.hasWorkPreferencesFilled
                        ) {
                          update({ isOpenJobSeekerApplyForJobPostModal: true });
                        }
                      }}
                    />
                  </div>
                </Tooltip>
              </div>
            </ClickAwayListener>
          )}
        </div>

        <S.JobPostCardPreviewMainBlockHeaderCompanyInfo>
          <S.JobPostCardPreviewMainBlockHeaderCompanyInfoLeft>
            {data?.company?.logo?.link || data?.company?.logoUrl ? (
              <img
                src={data.company?.logo?.link || data.company?.logoUrl}
                alt={data.company.name}
                style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover' as const }}
              />
            ) : (
              <CompanyLogoIcon />
            )}
            <S.CompanyInfoLeftBlock>
              <Link to={`/companies/${data?.company?.slug}`}>
                <S.CompanyInfoLeftBlockTitle>
                  {data?.company?.name || data?.companyNameLegacy || 'No company name'}
                </S.CompanyInfoLeftBlockTitle>
              </Link>
              <S.CompanyInfoLeftBlockSubTitle>
                {data?.location?.name}, {data?.location?.state?.name}
              </S.CompanyInfoLeftBlockSubTitle>
            </S.CompanyInfoLeftBlock>
          </S.JobPostCardPreviewMainBlockHeaderCompanyInfoLeft>
          <S.JobInfoBlock>
            <S.JobInfoBlockItem>
              <p>
                <span>Industry:</span> {data?.industry?.name || 'Industry not provided'}
              </p>
            </S.JobInfoBlockItem>
            <S.JobInfoBlockItem>
              <p>
                <span>Category:</span> {data?.jobCategory?.name || 'Category not provided'}
              </p>
            </S.JobInfoBlockItem>
          </S.JobInfoBlock>
        </S.JobPostCardPreviewMainBlockHeaderCompanyInfo>
      </S.JobPostCardPreviewMainBlockHeader>
      {Number(matchScore) || data?.score ? (
        <S.MatchScoreContainer>
          {Number(matchScore) ? (
            <>
              <S.MatchScoreText>{Number(matchScore)}% match based on your filters</S.MatchScoreText>
              <BorderLinearProgress
                variant="determinate"
                value={Number(matchScore) || data?.score}
                sx={{
                  width: '100%',
                  flex: '1 0 auto',

                  '.MuiLinearProgress-bar': {
                    backgroundColor: getMatchScoreColor(Number(matchScore)),
                  },
                }}
              />
            </>
          ) : null}

          {data?.score ? (
            <>
              <S.MatchScoreText>{data?.score}% match based on your profile</S.MatchScoreText>
              <BorderLinearProgress
                variant="determinate"
                value={data?.score}
                sx={{
                  width: '100%',
                  flex: '1 0 auto',

                  '.MuiLinearProgress-bar': {
                    backgroundColor: getMatchScoreColor(data?.score),
                  },
                }}
              />
            </>
          ) : null}
        </S.MatchScoreContainer>
      ) : null}
      <S.JobSummaryContainer>
        <S.JobSummaryTitle>Job Summary</S.JobSummaryTitle>
        <S.JobSummaryContent>
          <S.JobSummaryContentItemBox>
            <SalaryRangeIcon />
            <S.JobSummaryContentItemTextBox>
              <S.JobSummaryContentItemTextBoxTitle>
                Salary Range
              </S.JobSummaryContentItemTextBoxTitle>
              <S.JobSummaryContentItemTextBoxSubTitle>
                {!data.salaryMin && !data.salaryMax
                  ? 'Salary not specified'
                  : `$${data.salaryMin.toLocaleString()} - $${data.salaryMax.toLocaleString()}`}
              </S.JobSummaryContentItemTextBoxSubTitle>
            </S.JobSummaryContentItemTextBox>
          </S.JobSummaryContentItemBox>
          <S.JobSummaryContentItemBox>
            <PositionTypeIcon />
            <S.JobSummaryContentItemTextBox>
              <S.JobSummaryContentItemTextBoxTitle>
                Position Type
              </S.JobSummaryContentItemTextBoxTitle>
              <S.JobSummaryContentItemTextBoxSubTitle>
                {getPositionTypes(data?.positionType)}
              </S.JobSummaryContentItemTextBoxSubTitle>
            </S.JobSummaryContentItemTextBox>
          </S.JobSummaryContentItemBox>
          <S.JobSummaryContentItemBox>
            <LocationTypeIcon />
            <S.JobSummaryContentItemTextBox>
              <S.JobSummaryContentItemTextBoxTitle>
                Location Type
              </S.JobSummaryContentItemTextBoxTitle>
              <S.JobSummaryContentItemTextBoxSubTitle>
                {getJobLocation(data?.jobLocation)}
              </S.JobSummaryContentItemTextBoxSubTitle>
            </S.JobSummaryContentItemTextBox>
          </S.JobSummaryContentItemBox>
          <S.JobSummaryContentItemBox>
            <ExperienceLevelIcon />
            <S.JobSummaryContentItemTextBox>
              <S.JobSummaryContentItemTextBoxTitle>
                Experience Level
              </S.JobSummaryContentItemTextBoxTitle>
              <S.JobSummaryContentItemTextBoxSubTitle>
                {data?.experienceLevel?.name || 'No experience'}
              </S.JobSummaryContentItemTextBoxSubTitle>
            </S.JobSummaryContentItemTextBox>
          </S.JobSummaryContentItemBox>
          <S.JobSummaryContentItemBox>
            <LanguagesIcon />
            <S.JobSummaryContentItemTextBox>
              <S.JobSummaryContentItemTextBoxTitle>Languages</S.JobSummaryContentItemTextBoxTitle>
              <S.JobSummaryContentItemTextBoxSubTitle>
                {getLanguages(data?.languages)}
              </S.JobSummaryContentItemTextBoxSubTitle>
            </S.JobSummaryContentItemTextBox>
          </S.JobSummaryContentItemBox>
          <S.JobSummaryContentItemBox>
            <HiringNeedIcon />
            <S.JobSummaryContentItemTextBox>
              <S.JobSummaryContentItemTextBoxTitle>
                Immediate Hiring Need
              </S.JobSummaryContentItemTextBoxTitle>
              <S.JobSummaryContentItemTextBoxSubTitle>
                {data?.immHiringNeed ? 'Yes' : 'No'}
              </S.JobSummaryContentItemTextBoxSubTitle>
            </S.JobSummaryContentItemTextBox>
          </S.JobSummaryContentItemBox>
          <S.JobSummaryContentItemBox>
            <EducationLevelIcon />
            <S.JobSummaryContentItemTextBox>
              <S.JobSummaryContentItemTextBoxTitle>
                Education Level
              </S.JobSummaryContentItemTextBoxTitle>
              <S.JobSummaryContentItemTextBoxSubTitle>
                {data?.education?.name || 'No education'}
              </S.JobSummaryContentItemTextBoxSubTitle>
            </S.JobSummaryContentItemTextBox>
          </S.JobSummaryContentItemBox>
          <S.JobSummaryContentItemBox>
            <EducationLevelIcon />
            <S.JobSummaryContentItemTextBox>
              <S.JobSummaryContentItemTextBoxTitle>
                Years of Experience
              </S.JobSummaryContentItemTextBoxTitle>
              <S.JobSummaryContentItemTextBoxSubTitle>
                {yearsOfExperienceOptions.filter((item) => {
                  return (
                    item.experienceYearsMin === data?.experienceYearsMin &&
                    item.experienceYearsMax === data?.experienceYearsMax
                  );
                })?.[0]?.value || 'No Experience'}
              </S.JobSummaryContentItemTextBoxSubTitle>
            </S.JobSummaryContentItemTextBox>
          </S.JobSummaryContentItemBox>
        </S.JobSummaryContent>
      </S.JobSummaryContainer>
      <S.JobDetailsContainer>
        <S.JobDetailsTitle>Job Details</S.JobDetailsTitle>
        <S.JobDetailsContent>
          <S.ReusableTextBlock>
            <S.ReusableTextBlockTitle>Roles and Responsibilities</S.ReusableTextBlockTitle>
            <S.ReusableTextBlockDesc>
              {data?.rolesAndResponsibilities || 'No responsibilities'}
            </S.ReusableTextBlockDesc>
          </S.ReusableTextBlock>
          <S.ReusableTextBlock>
            <S.ReusableTextBlockTitle>Qualifications & Skills</S.ReusableTextBlockTitle>
            <S.JobDetailsContentQualifications>
              <S.JobDetailsContentQualificationsItem>
                <S.JobDetailsContentQualificationsItemTitle>
                  Qualifications
                </S.JobDetailsContentQualificationsItemTitle>
                {data?.qualifications?.length > 0
                  ? data?.qualifications?.map((qual) => {
                      return (
                        <S.JobDetailsContentQualificationsItemBox key={qual.id}>
                          <S.JobDetailsContentQualificationsItemBoxImportance>
                            {Array.from({ length: 5 }).map((_, index) => {
                              return index < qual.importance ? (
                                // eslint-disable-next-line react/no-array-index-key
                                <div key={index}>
                                  <FilledRectangleIcon />
                                </div>
                              ) : (
                                // eslint-disable-next-line react/no-array-index-key
                                <div key={index}>
                                  <OutlinedRectangleIcon />
                                </div>
                              );
                            })}
                          </S.JobDetailsContentQualificationsItemBoxImportance>
                          <S.JobDetailsContentQualificationsItemBoxName>
                            {qual?.qualification.name}
                          </S.JobDetailsContentQualificationsItemBoxName>
                        </S.JobDetailsContentQualificationsItemBox>
                      );
                    })
                  : 'No qualifications'}
              </S.JobDetailsContentQualificationsItem>
              <S.JobDetailsContentQualificationsItem>
                <S.JobDetailsContentQualificationsItemTitle>
                  Technical Skills
                </S.JobDetailsContentQualificationsItemTitle>
                {data?.skills?.length > 0 &&
                data?.skills?.filter((item) => {
                  return item.skill.group === 'Hard Skills';
                }).length > 0
                  ? data?.skills
                      ?.filter((item) => {
                        return item.skill.group === 'Hard Skills';
                      })
                      ?.map((hardSkill) => {
                        return (
                          <S.JobDetailsContentQualificationsItemBox key={hardSkill.id}>
                            <S.JobDetailsContentQualificationsItemBoxImportance>
                              {Array.from({ length: 5 }).map((_, index) => {
                                return index < hardSkill.importance ? (
                                  // eslint-disable-next-line react/no-array-index-key
                                  <div key={index}>
                                    <FilledRectangleIcon />
                                  </div>
                                ) : (
                                  // eslint-disable-next-line react/no-array-index-key
                                  <div key={index}>
                                    <OutlinedRectangleIcon />
                                  </div>
                                );
                              })}
                            </S.JobDetailsContentQualificationsItemBoxImportance>
                            <S.JobDetailsContentQualificationsItemBoxName>
                              {hardSkill?.skill?.name}
                            </S.JobDetailsContentQualificationsItemBoxName>
                          </S.JobDetailsContentQualificationsItemBox>
                        );
                      })
                  : 'No technical skills'}
              </S.JobDetailsContentQualificationsItem>
              <S.JobDetailsContentQualificationsItem>
                <S.JobDetailsContentQualificationsItemTitle>
                  Soft Skills
                </S.JobDetailsContentQualificationsItemTitle>
                {data?.skills?.length > 0 &&
                data?.skills?.filter((item) => {
                  return item.skill.group === 'Soft Skills';
                }).length > 0
                  ? data?.skills
                      ?.filter((item) => {
                        return item.skill.group === 'Soft Skills';
                      })
                      ?.map((softSkill) => {
                        return (
                          <S.JobDetailsContentQualificationsItemBox key={softSkill.id}>
                            <S.JobDetailsContentQualificationsItemBoxImportance>
                              {Array.from({ length: 5 }).map((_, index) => {
                                return index < softSkill.importance ? (
                                  // eslint-disable-next-line react/no-array-index-key
                                  <div key={index}>
                                    <FilledRectangleIcon />
                                  </div>
                                ) : (
                                  // eslint-disable-next-line react/no-array-index-key
                                  <div key={index}>
                                    <OutlinedRectangleIcon />
                                  </div>
                                );
                              })}
                            </S.JobDetailsContentQualificationsItemBoxImportance>
                            <S.JobDetailsContentQualificationsItemBoxName>
                              {softSkill?.skill?.name}
                            </S.JobDetailsContentQualificationsItemBoxName>
                          </S.JobDetailsContentQualificationsItemBox>
                        );
                      })
                  : 'No soft skills'}
              </S.JobDetailsContentQualificationsItem>
            </S.JobDetailsContentQualifications>
          </S.ReusableTextBlock>
        </S.JobDetailsContent>
      </S.JobDetailsContainer>
      <S.CompanyContainer>
        <S.CompanyTitle>Company</S.CompanyTitle>
        <S.CompanyContent>
          <S.ReusableTextBlock>
            <S.ReusableTextBlockTitle>Overview</S.ReusableTextBlockTitle>
            <S.ReusableTextBlockDesc>
              {data?.companyOverview || 'No overview'}
            </S.ReusableTextBlockDesc>
          </S.ReusableTextBlock>
          <S.ReusableTextBlock>
            <S.ReusableTextBlockTitle>Benefits</S.ReusableTextBlockTitle>
            <S.ReusableTextBlockDesc>
              {data?.companyBenefits || 'No benefits'}
            </S.ReusableTextBlockDesc>
          </S.ReusableTextBlock>
          <S.ReusableTextBlock>
            <S.ReusableTextBlockTitle>Diversity and Inclusion</S.ReusableTextBlockTitle>
            <S.ReusableTextBlockDesc>
              {data?.diversityStatement || 'No diversity and inclusion'}
            </S.ReusableTextBlockDesc>
          </S.ReusableTextBlock>
        </S.CompanyContent>
      </S.CompanyContainer>
      <S.FAQContainer>
        <S.FAQTitle>FAQs</S.FAQTitle>
        <S.FAQContent>
          <S.FAQWrapper>
            {data?.faq?.length > 0
              ? data?.faq?.map((item, index) => {
                  const { answer, question } = item;
                  const expandedId = `panel${index + 1}`;
                  const ariaControls = `panel${index + 1}bh-content`;
                  const summaryId = `panel${index + 1}bh-header`;
                  return (
                    <S.CustomAccordion
                      expanded={expanded === expandedId}
                      onChange={handleChange(expandedId)}
                      key={expandedId}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={ariaControls}
                        id={summaryId}
                      >
                        <S.AccordionTitle component="span">{question}</S.AccordionTitle>
                      </AccordionSummary>
                      <AccordionDetails>
                        <S.AccordionDesc>{answer}</S.AccordionDesc>
                      </AccordionDetails>
                    </S.CustomAccordion>
                  );
                })
              : 'No frequently asked questions'}
          </S.FAQWrapper>
        </S.FAQContent>
      </S.FAQContainer>
      <S.ApplicationProcessContainer>
        <S.ApplicationProcessTitle>Application Process</S.ApplicationProcessTitle>
        <S.ApplicationProcessContent>
          <S.ReusableTextBlock>
            <S.ReusableTextBlockDesc>
              {data?.applicationProcess || 'No application process information'}
            </S.ReusableTextBlockDesc>
          </S.ReusableTextBlock>
          {data?.applicationStatus?.statusForCandidate ? (
            <StatusBlock appStatus={data?.applicationStatus?.statusForCandidate} />
          ) : (
            <ClickAwayListener onClickAway={handleTooltipBottomClose}>
              <div>
                <Tooltip
                  onClose={handleTooltipBottomClose}
                  open={openBottom}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  slotProps={{
                    popper: {
                      disablePortal: true,
                    },
                  }}
                  title={
                    !jobSeekerProfile || !jobSeekerProfile.hasWorkPreferencesFilled ? (
                      <div style={{ padding: '5px' }}>
                        <h3 style={{ marginBottom: '8px' }}>Incomplete Profile</h3>
                        <p>
                          You can’t apply for this position because your profile is incomplete.
                          Please complete your profile before applying.
                        </p>
                      </div>
                    ) : (
                      ''
                    )
                  }
                  arrow
                  placement="top"
                >
                  <div
                    onClick={isPublic ? undefined : handleTooltipBottomOpen}
                    style={{ width: 'max-content', height: 'max-content' }}
                  >
                    <Button
                      variant="contained"
                      text={isPublic ? 'Sign In to Apply' : 'Apply now'}
                      style={{ width: 'max-content', height: 'max-content' }}
                      onClick={() => {
                        if (isPublic) {
                          navigate('/auth/login');
                        } else if (
                          update &&
                          jobSeekerProfile &&
                          jobSeekerProfile.hasWorkPreferencesFilled
                        ) {
                          update({ isOpenJobSeekerApplyForJobPostModal: true });
                        }
                      }}
                    />
                  </div>
                </Tooltip>
              </div>
            </ClickAwayListener>
          )}
        </S.ApplicationProcessContent>
      </S.ApplicationProcessContainer>
      <JobSeekerApplyForJobPostModal jobPostId={data.id} getJobPostInfoById={getJobPostInfoById} />
    </S.CandidateProfileBlock>
  );
};

export default ViewJobPostBlock;
